"use client";

/**
 * The typing half of address autocomplete: debounce, fetch, stale-guard, and
 * the Places session token that keeps the whole sequence on one bill.
 *
 * Kept apart from the component so focus-stability is easy to honour — the
 * hook owns no DOM, returns no `disabled` flag, and never asks the field to
 * remount. `isSearching` is exposed for a spinner and MUST NOT be bound to the
 * input's `disabled`/`readOnly`: disabling a focused field blurs it, which
 * drops every character typed after the flip.
 *
 * Posts to `/api/address/lookup` — a host app is expected to serve that exact
 * path (a thin route delegating to this package's `provider`), the same way
 * Hub does.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { AddressSuggestion } from "../normalize.js";
import type { CrmAddressFields } from "../canonical.js";
import { MIN_ADDRESS_QUERY_LENGTH } from "../provider.js";

/** Long enough that a typist doesn't fire a billed request per character. */
export const ADDRESS_SEARCH_DEBOUNCE_MS = 300;

interface DetailsResponse {
  address: CrmAddressFields;
  formattedAddress: string;
  placeId: string;
}

/**
 * Mint a Places session token.
 *
 * One token spans every keystroke of one address plus the details call that
 * ends it, so Google bills the sequence as a single session. A fresh one is
 * minted after each selection because a spent token is billed as if absent.
 */
function mintSessionToken(): string {
  return crypto.randomUUID();
}

export interface UseAddressAutocompleteResult {
  suggestions: AddressSuggestion[];
  isSearching: boolean;
  /** True once a lookup failed; the caller falls back to plain manual entry. */
  hasFailed: boolean;
  /** Feed every keystroke here. Debouncing and stale-guarding are internal. */
  search: (query: string) => void;
  /** Resolve a picked suggestion to CRM fields. Returns null if it can't. */
  select: (placeId: string) => Promise<CrmAddressFields | null>;
  /** Drop the list without issuing a request (blur, escape, after a pick). */
  clear: () => void;
}

export function useAddressAutocomplete(): UseAddressAutocompleteResult {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  /** Newest-request token: a slow earlier reply must not overwrite a newer one. */
  const requestTokenRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const sessionTokenRef = useRef<string>(mintSessionToken());
  /** Guards every setState against a component that unmounted mid-flight. */
  const mountedRef = useRef(true);

  const cancelPending = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  // Started work must be stoppable: clear the timer and abort the in-flight
  // request on unmount, or a reply lands on a dead component.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelPending();
    };
  }, [cancelPending]);

  const clear = useCallback(() => {
    cancelPending();
    requestTokenRef.current += 1;
    setSuggestions([]);
    setIsSearching(false);
  }, [cancelPending]);

  const search = useCallback(
    (query: string) => {
      cancelPending();
      const trimmed = query.trim();
      const token = ++requestTokenRef.current;

      if (trimmed.length < MIN_ADDRESS_QUERY_LENGTH) {
        setSuggestions([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      debounceRef.current = setTimeout(() => {
        const controller = new AbortController();
        abortRef.current = controller;

        void fetch("/api/address/lookup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            mode: "suggest",
            query: trimmed,
            sessionToken: sessionTokenRef.current,
          }),
          signal: controller.signal,
        })
          .then(async (response) => {
            if (!response.ok) throw new Error(`status ${response.status}`);
            const body: unknown = await response.json();
            const next =
              body && typeof body === "object" && "suggestions" in body
                ? ((body as { suggestions: AddressSuggestion[] }).suggestions ?? [])
                : [];
            if (token !== requestTokenRef.current || !mountedRef.current) return;
            setSuggestions(next);
            setHasFailed(false);
          })
          .catch((error: unknown) => {
            if (controller.signal.aborted) return;
            if (token !== requestTokenRef.current || !mountedRef.current) return;
            // No suggestions is not an error to the user; it just means the
            // control quietly stops helping and they type it themselves.
            setSuggestions([]);
            setHasFailed(true);
            void error;
          })
          .finally(() => {
            // Always clears, on every path — an unsettled loading flag is
            // invisible and permanent.
            if (token !== requestTokenRef.current || !mountedRef.current) return;
            setIsSearching(false);
          });
      }, ADDRESS_SEARCH_DEBOUNCE_MS);
    },
    [cancelPending]
  );

  const select = useCallback(
    async (placeId: string): Promise<CrmAddressFields | null> => {
      cancelPending();
      requestTokenRef.current += 1;
      const spentToken = sessionTokenRef.current;
      // The session ends with this details call; the next address needs its own.
      sessionTokenRef.current = mintSessionToken();

      try {
        const response = await fetch("/api/address/lookup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ mode: "details", placeId, sessionToken: spentToken }),
        });
        if (!response.ok) throw new Error(`status ${response.status}`);
        const body = (await response.json()) as DetailsResponse;
        return body.address ?? null;
      } catch {
        if (mountedRef.current) setHasFailed(true);
        return null;
      } finally {
        if (mountedRef.current) {
          setSuggestions([]);
          setIsSearching(false);
        }
      }
    },
    [cancelPending]
  );

  return { suggestions, isSearching, hasFailed, search, select, clear };
}
